import React, { useEffect, useState } from 'react';
import FilterDropdown from '../Filter/components/FilterDropdown';
import { translate } from '../../services/translate';
import { licensePlateRecognitionService } from '../../services/central-api/license-plate-recognition';
import { UseFormReturn } from 'react-hook-form';
import FormDropDownPicker from '../FormDropDownPicker';
import { StyleProp, TextStyle } from 'react-native';
import { useCurrentApp } from '../../context/CurrentApp';

type Props = {
    zIndex: number;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<TextStyle>;
    companyId?: number;
    showLabel?: boolean;
    requiresCompanyId?: boolean;
} & ({
    form?: undefined;
    fieldName?: undefined;
    value: string[];
    setValue: React.Dispatch<React.SetStateAction<string[]>>;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: undefined;
    setValue?: undefined;
});

export default function CamerasDropdown({ value, setValue, zIndex, form, fieldName, required, labelStyle, viewStyle, companyId, showLabel, requiresCompanyId }: Props) {
    const { isParentApp } = useCurrentApp();
    const [items, setItems] = useState<{ label: string; value: string; }[]>([]);
    const [searchText, setSearchText] = useState('');
    const [filteredItems, setFilteredItems] = useState(items);
    const [filterTimeout, setFilterTimeout] = useState<NodeJS.Timeout>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    async function getItems() {
        try {
            const cameras = await licensePlateRecognitionService.getCameras(isParentApp && companyId && requiresCompanyId ? companyId : undefined);
            setItems(cameras.map((camera) => {
                return {
                    value: camera.id,
                    label: camera.title
                };
            }));
            setFilteredItems(cameras.map((camera) => {
                return {
                    value: camera.id,
                    label: camera.title
                };
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function handleSearch(text: string) {
        setIsLoading(true);
        setFilteredItems([]);
        if (filterTimeout) {
            clearTimeout(filterTimeout);
        }
        const timeout = setTimeout(() => {
            setSearchText(text);
            const lower = text.toLowerCase().trim();

            if (!lower) {
                setFilteredItems(items);
                setIsLoading(false);
                return;
            }

            const filtered = items.filter(item => {
                const label = item.label.toLowerCase();
                const uuid = item.value.toLowerCase();

                return (
                    label.includes(lower) ||
                    uuid.includes(lower)
                );
            });
            const selectedValues = form?.getValues(fieldName) ?? [];
            const selectedItems = items.filter(item =>
                selectedValues.includes(item.value)
            );

            setFilteredItems([
                ...selectedItems,
                ...filtered.filter(
                    item => !selectedValues.includes(item.value)
                )
            ]);
            setIsLoading(false);
        }, 500);
        setFilterTimeout(timeout);
    }

    useEffect(() => {
        if (requiresCompanyId && isParentApp && !companyId) {
            return;
        }

        getItems();
    }, [isParentApp, companyId]);

    if (form && fieldName) {
        return <FormDropDownPicker
            label={showLabel ? translate('cameras') : undefined}
            placeholder={translate('cameras')}
            zIndex={zIndex}
            labelStyle={labelStyle}
            viewStyle={viewStyle}
            required={required}
            fieldName={fieldName}
            form={form}
            multiple={true}
            mode='BADGE'
            items={filteredItems}
            onChangeSearchText={handleSearch}
            disableLocalSearch={true}
            searchTextInputProps={{
                value: searchText
            }}
            loading={isLoading}
        />;
    } else if (value !== undefined && setValue !== undefined) {
        return (
            <FilterDropdown
                label={showLabel ? translate('cameras') : undefined}
                placeholder={translate('cameras')}
                multiple={true}
                mode='BADGE'
                setValue={setValue}
                value={value}
                zIndex={zIndex}
                items={filteredItems}
                onChangeSearchText={handleSearch}
                disableLocalSearch={true}
                searchTextInputProps={{
                    value: searchText
                }}
                loading={isLoading}
            />
        );
    }
}
